import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  current,
} from "@reduxjs/toolkit";
import {
  hideSbcSet as hideSbcSetRequest,
  markSbcSetAsFavorite as markSbcSetAsFavoriteRequest,
  markSbcSetSolved as markSbcSetSolvedRequest,
} from "@repo/shared/api";
import {
  SBC_CATEGORIES,
  SbcListControls,
  applySbcListControls,
  sbcSetStatus,
} from "@repo/shared/utils";

import { APIStatus } from "../../api/APIStatus.type";
import { getSbcSets } from "../../api/privateApi";
import {
  RepeatabilityMode,
  SBCRewardType,
} from "../../api/privateRequests/sbcs/getSbcSets";
import { IRootState } from "../store";
import { Platform } from "../user/userSlice";

export type SBCCategory = {
  // Toolbar tabs keep their English label (an i18n key lookup); EA categories
  // carry the localized name the set list sends.
  label: string;
  // -1 = All, undefined = Favorites, > 0 = EA category id.
  value?: number;
};

export const ALL_CATEGORY: SBCCategory = { label: "All", value: -1 };
export const FAVORITES_CATEGORY: SBCCategory = { label: "Favorites" };

type SBCSetSortOptionValues =
  | "most-upvoted"
  | "price-high-to-low"
  | "price-low-to-high"
  | "in-progress"
  | "expiration"
  | "newest"
  | "meta-rating"
  | "trending";

export type SBCSetSortOption = {
  label:
    | "Most Upvoted"
    | "Price - High to Low"
    | "Price - Low to High"
    | "In-progress"
    | "Expiration"
    | "Newest"
    | "Meta Rating"
    | "Trending";
  value: SBCSetSortOptionValues;
};

export type SbcSet = {
  kind?: "NORMAL" | "STREAMLINE";
  pointsRequired?: number;
  reward?: SBCRewardType;
  id: number;
  imageURL: string;
  name: string;
  slug?: string;
  description: string;
  pcPrice: number;
  psPrice: number;
  message: string;
  sbcsCount: number;
  startTime: number;
  endTime: number;
  upVotes: number;
  downVotes: number;
  isNew: boolean;
  isHidden: boolean;
  isFavorite: boolean;
  hasRefreshed: boolean;
  repeatable: boolean;
  refreshInterval?: number;
  repeats: number;
  categoryId: number;
  categoryName?: string;
  repeatabilityMode: RepeatabilityMode;
  unsolvable?: boolean;
  solvedCount: number;
  solved?: boolean;
  wasUpvoted?: boolean;
  wasDownvoted?: boolean;
  remainingCost: number | null;
  trendingScore?: number;
};

type SBCState = {
  sbcsSets: {
    data: SbcSet[];
    status: APIStatus;
  };
  currentCategory: SBCCategory;
  currentSort: SBCSetSortOption;
  searchTerm: string;
};

export type SetVote = "UPVOTE" | "DOWNVOTE";

/**
 * Optimistic vote arithmetic, shared by the set list and the set page: a
 * repeated vote undoes itself, and the opposite vote takes its count with it.
 */
export function applyVote(
  sbcSet: Pick<SbcSet, "upVotes" | "downVotes" | "wasUpvoted" | "wasDownvoted">,
  vote: SetVote,
) {
  if (vote === "UPVOTE") {
    const undo = sbcSet.wasUpvoted;
    sbcSet.upVotes += undo ? -1 : 1;
    if (sbcSet.wasDownvoted) sbcSet.downVotes -= 1;
    sbcSet.wasDownvoted = false;
    sbcSet.wasUpvoted = !undo;
  } else {
    const undo = sbcSet.wasDownvoted;
    sbcSet.downVotes += undo ? -1 : 1;
    if (sbcSet.wasUpvoted) sbcSet.upVotes -= 1;
    sbcSet.wasUpvoted = false;
    sbcSet.wasDownvoted = !undo;
  }
}

const initialState: SBCState = {
  sbcsSets: {
    status: "idle",
    data: [],
  },
  currentCategory: ALL_CATEGORY,
  currentSort: {
    label: "Newest",
    value: "newest",
  },
  searchTerm: "",
};

export const markSbcSetSolved = createAsyncThunk(
  "mark-sbc-set-solved",
  async (
    {
      setId,
      didComplete,
    }: {
      setId: number;
      didComplete: boolean;
    },
    { dispatch },
  ) => {
    dispatch(updateSbcSetSolved({ setId, didComplete }));
    markSbcSetSolvedRequest(setId, didComplete);
  },
);

export const markSbcSolved = createAsyncThunk(
  "update-sbc-solved",
  async (
    {
      setId,
      sbcId,
      solved,
    }: {
      setId: number;
      sbcId: number;
      solved: boolean;
    },
    { getState, dispatch },
  ) => {
    const state = getState() as IRootState;

    const sbc = state.sbcsReducer.sbcs.find((sbc) => sbc.id === sbcId);
    const platform = state.userReducer.user.platform || "Playstation";
    dispatch(
      updateSbcSolved({
        setId,
        solved,
        priceOfSbc: sbc.price,
        platfrom: platform,
      }),
    );
  },
);

export const hideSbcSet = createAsyncThunk(
  "hide-sbc-set",
  async (
    {
      setId,
      isHidden,
    }: {
      setId: number;
      isHidden: boolean;
    },
    { dispatch },
  ) => {
    dispatch(updateSbcSetHidden({ setId, isHidden }));
    hideSbcSetRequest(setId, isHidden);
  },
);
export const markSbcSetAsFavorite = createAsyncThunk(
  "hide-sbc-set",
  async (
    {
      setId,
      isFavorite,
    }: {
      setId: number;
      isFavorite: boolean;
    },
    { dispatch },
  ) => {
    dispatch(updateSbcSetFavorite({ setId, isFavorite }));
    markSbcSetAsFavoriteRequest(setId, isFavorite);
  },
);

export const fetchSbcsSets = createAsyncThunk("sbc-sets", async () => {
  return getSbcSets();
});
const getCopyOfSet = (state: SBCState, setId: number) => {
  const sbcSetsCopy = [...current(state.sbcsSets.data)];
  let sbcSetIndex = sbcSetsCopy.findIndex((sbcSet) => sbcSet.id === setId);
  return {
    sbcSetIndex,
    sbcSetCopy: { ...sbcSetsCopy[sbcSetIndex] },
    sbcSetsCopy,
  };
};
const sbcSetsSlice = createSlice({
  name: "sbc-sets",
  initialState,
  reducers: {
    updateSbcSolved(
      state,
      action: PayloadAction<{
        setId: number;
        solved: boolean;
        priceOfSbc: number;
        platfrom: Platform;
      }>,
    ) {
      const {
        setId,
        solved: solvedPayload,
        priceOfSbc,
        platfrom,
      } = action.payload;
      const { sbcSetsCopy, sbcSetIndex, sbcSetCopy } = getCopyOfSet(
        state,
        setId,
      );
      if (solvedPayload && sbcSetCopy.solvedCount < +sbcSetCopy.sbcsCount) {
        sbcSetCopy.solvedCount += 1;
      } else {
        sbcSetCopy.solvedCount -= 1;
      }
      sbcSetCopy.solved = sbcSetCopy.solvedCount === +sbcSetCopy.sbcsCount;
      if (sbcSetCopy.remainingCost) {
        if (solvedPayload) {
          sbcSetCopy.remainingCost -= priceOfSbc;
        } else {
          if (sbcSetCopy.solvedCount === 0) {
            sbcSetCopy.remainingCost = null;
          } else {
            sbcSetCopy.remainingCost += priceOfSbc;
          }
        }
      } else {
        sbcSetCopy.remainingCost =
          platfrom === "Playstation"
            ? sbcSetCopy.psPrice - priceOfSbc
            : sbcSetCopy.pcPrice - priceOfSbc;
      }

      sbcSetsCopy[sbcSetIndex] = sbcSetCopy;
      state.sbcsSets.data = sbcSetsCopy;
    },
    updateSbcSetSolved(state, action) {
      const { setId, didComplete: solvedPayload } = action.payload;
      const { sbcSetsCopy, sbcSetIndex, sbcSetCopy } = getCopyOfSet(
        state,
        setId,
      );
      if (solvedPayload) {
        sbcSetCopy.solvedCount = +sbcSetCopy.sbcsCount;
        sbcSetCopy.remainingCost = 0;
      } else {
        sbcSetCopy.solvedCount = 0;
      }
      sbcSetCopy.solved = solvedPayload;
      sbcSetsCopy[sbcSetIndex] = sbcSetCopy;

      state.sbcsSets.data = sbcSetsCopy;
    },
    upvote(state, action: PayloadAction<{ setId: number }>) {
      const sbcSet = state.sbcsSets.data.find(
        (sbcSet) => +sbcSet.id === action.payload.setId,
      );
      if (sbcSet) applyVote(sbcSet, "UPVOTE");
    },
    downvote(state, action: PayloadAction<{ setId: number }>) {
      const sbcSet = state.sbcsSets.data.find(
        (sbcSet) => +sbcSet.id === action.payload.setId,
      );
      if (sbcSet) applyVote(sbcSet, "DOWNVOTE");
    },
    clearSbcSets(state) {
      state.sbcsSets.data = [];
    },
    updateCategory(state, action: PayloadAction<SBCCategory>) {
      state.currentCategory = action.payload;
    },
    updateSort(state, action: PayloadAction<SBCSetSortOption>) {
      state.currentSort = action.payload;
    },
    updateSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
    },
    updateSbcSetHidden(
      state,
      action: PayloadAction<{
        setId: number;
        isHidden: boolean;
      }>,
    ) {
      state.sbcsSets.data = state.sbcsSets.data.map((set) =>
        set.id === action.payload.setId
          ? { ...set, isHidden: action.payload.isHidden }
          : set,
      );
    },
    updateSbcSetFavorite(
      state,
      action: PayloadAction<{
        setId: number;
        isFavorite: boolean;
      }>,
    ) {
      state.sbcsSets.data = state.sbcsSets.data.map((set) =>
        set.id === action.payload.setId
          ? { ...set, isFavorite: action.payload.isFavorite }
          : set,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSbcsSets.pending, (state) => {
        state.sbcsSets.status = "pending";
      })
      .addCase(fetchSbcsSets.fulfilled, (state, action) => {
        state.sbcsSets.status = "fulfilled";
        if (action.payload) state.sbcsSets.data = action.payload;
      })
      .addCase(fetchSbcsSets.rejected, (state) => {
        state.sbcsSets.status = "rejected";
      });
  },
});

// All / Favorites, then every EA category the loaded sets use, in id order,
// so the tabs follow what is live. The English list only covers a set that
// arrived without its localized name.
export const getSbcSetCategoriesSelector = createSelector(
  [(state: IRootState) => state.sbcsSetsReducer.sbcsSets.data],
  (sbcSets): SBCCategory[] => {
    const namesById = new Map<number, string>();
    for (const set of sbcSets) {
      if (!(set.categoryId > 0) || namesById.has(set.categoryId)) continue;
      namesById.set(
        set.categoryId,
        set.categoryName ??
          SBC_CATEGORIES.find((c) => c.value === set.categoryId)?.label ??
          String(set.categoryId),
      );
    }
    return [
      ALL_CATEGORY,
      FAVORITES_CATEGORY,
      ...[...namesById]
        .sort(([a], [b]) => a - b)
        .map(([value, label]) => ({ label, value })),
    ];
  },
);

// Memoized: builds four new arrays, so an unmemoized version re-sorts the
// whole list on every dispatched action.
export const getFilteredSbcSetsSelector = createSelector(
  [
    (state: IRootState) => state.sbcsSetsReducer.sbcsSets.data,
    (state: IRootState) => state.sbcsSetsReducer.currentCategory,
    (state: IRootState) => state.sbcsSetsReducer.currentSort,
    (state: IRootState) => state.sbcsSetsReducer.searchTerm,
    (state: IRootState) => state.userReducer.user.platform,
  ],
  (sbcSets, currentCategory, currentSort, searchTerm, platform) => {
    const notCompletedSbcs: SbcSet[] = [];
    const completedSbcs: SbcSet[] = [];
    const hiddenSbcs: SbcSet[] = [];

    sbcSets.forEach((sbc) => {
      const status = sbcSetStatus(sbc);
      if (status === "hidden") hiddenSbcs.push(sbc);
      else if (status === "completed") completedSbcs.push(sbc);
      else notCompletedSbcs.push(sbc);
    });

    // Translate web's toolbar state into shared list controls. "All" (-1) and
    // "Favorites" (no value) are toolbar tabs, not real categories: All -> no
    // category filter, Favorites -> the favoritesOnly axis.
    const controls: SbcListControls = {
      categoryId:
        currentCategory.value && currentCategory.value > 0
          ? currentCategory.value
          : null,
      favoritesOnly: currentCategory.label === "Favorites",
      sort: currentSort.value,
      searchText: searchTerm,
      platform: platform === "PC" ? "PC" : "Playstation",
    };

    return {
      allSbcs: applySbcListControls(sbcSets, controls),
      notCompletedSbcs: applySbcListControls(notCompletedSbcs, controls),
      completedSbcs: applySbcListControls(completedSbcs, controls),
      hiddenSbcs: applySbcListControls(hiddenSbcs, controls),
    };
  },
);

export const {
  clearSbcSets,
  updateSbcSolved,
  updateSbcSetSolved,
  updateSbcSetHidden,
  updateSbcSetFavorite,
  upvote,
  downvote,
  updateCategory,
  updateSort,
  updateSearchTerm,
} = sbcSetsSlice.actions;
export const getSbcSetsSelector = (state: IRootState) => state.sbcsSetsReducer;
export default sbcSetsSlice.reducer;
